import { IoMdTime } from "react-icons/io";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight, MdPlayArrow, MdAssignment, MdVideoLibrary, MdDownload, MdPlayCircleFilled, MdClose, MdPeople, MdAccessTime } from "react-icons/md";
import { FiMenu, FiX, FiChevronUp, FiChevronDown, FiHome, FiFileText, FiUser, FiSettings } from "react-icons/fi";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { axiosInstance } from '../../../utils/axios';
import { BASE_URL } from '../../../utils/config';
import Blocks from "../../blocks";
import { useHistory } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Line, LabelList } from 'recharts';
import BbbRecordingPlayer from './BbbRecordingPlayer';
const tabList = [
  "Learning Effectiveness",
  "Student details",
  "Assignments",
  "Notes",
  "Recordings",
];

const PageTeachersDashboard = () => {
  const { classId } = useParams();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(0);
  const [classDetails, setClassDetails] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [students, setStudents] = useState([]);
  const [learningEffectiveness, setLearningEffectiveness] = useState(null);
  const [classPlan, setClassPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [virtualClasses, setVirtualClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [recentContent, setRecentContent] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [recordingsLoading, setRecordingsLoading] = useState(false);
  const [bbbRecordings, setBbbRecordings] = useState([]);
  const [bbbRecordingsLoading, setBbbRecordingsLoading] = useState(false);
  const [playbackRecordId, setPlaybackRecordId] = useState(null);
  const [playbackMeetingName, setPlaybackMeetingName] = useState(null);
  const [sessionAnalyticsBody, setSessionAnalyticsBody] = useState(null);
  const [questionsList, setQuestionsList] = useState([]);
  const [doubtsData, setDoubtsData] = useState({ total: 0, answered: 0, notAnswered: 0 });
  const [attentiveData, setAttentiveData] = useState(null);
  const [absentStudentsList, setAbsentStudentsList] = useState([]);
  const [leastAttentiveStudents, setLeastAttentiveStudents] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [mobileCardsOpen, setMobileCardsOpen] = useState({
    analytics: true,
    classPlan: true,
    doubts: true,
  });
  const { keycloak } = useKeycloak();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Core data - parallel fetch
        const [
          classResponse,
          attendanceResponse,
          studentsResponse,
          effectivenessResponse,
        ] = await Promise.all([
          axiosInstance.get(`/${classId}`),
          axiosInstance.get(`teachers-dashboard/present-students/${classId}`),
          axiosInstance.get(`teachers/virtual-class/${classId}/students`),
          axiosInstance.get(`/teachers-dashboard/class/${classId}/sa`),
        ]);

        setClassDetails(classResponse.data);
        setAttendanceData(attendanceResponse.data.body?.[0] || null);
        setStudents(studentsResponse.data.body || []);
        setLearningEffectiveness(effectivenessResponse.data.data);
        setSessionAnalyticsBody(effectivenessResponse.data.body);

        if (classResponse.data.classPlan) {
          setClassPlan(classResponse.data.classPlan);
        }

        // Secondary data - parallel fetch (non-blocking)
        const secondaryFetches = [
          // Doubts / Questions
          axiosInstance.get(`/questions/${classId}`)
            .then(res => {
              const questions = res.data.body || [];
              setQuestionsList(questions);
              const answered = questions.filter(q => q.answer !== null && q.answer !== undefined).length;
              setDoubtsData({
                total: questions.length,
                answered: answered,
                notAnswered: questions.length - answered,
              });
            })
            .catch(e => console.warn("Could not fetch doubts:", e)),

          // Attentive students (for Attendance Verification)
          axiosInstance.get(`/teachers-dashboard/attentive-students/${classId}`)
            .then(res => {
              setAttentiveData(res.data.body?.[0] || null);
            })
            .catch(e => console.warn("Could not fetch attentive data:", e)),

          // Absent students list
          axiosInstance.get(`/teachers-dashboard/absent-students/${classId}`)
            .then(res => {
              setAbsentStudentsList(res.data.body || []);
            })
            .catch(e => console.warn("Could not fetch absent students:", e)),

          // Least attentive students (for student details tab)
          axiosInstance.get(`/teachers-dashboard/least-attentive-students/${classId}`)
            .then(res => {
              setLeastAttentiveStudents(res.data.body || []);
            })
            .catch(e => console.warn("Could not fetch least attentive:", e)),

          // Class quizzes
          axiosInstance.get(`/teachers/class-quiz/class/${classId}`)
            .then(res => {
              setQuizzes(res.data.body || []);
            })
            .catch(e => console.warn("Could not fetch quizzes:", e)),
        ];

        await Promise.allSettled(secondaryFetches);

      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchRecordings = async () => {
      try {
        setRecordingsLoading(true);
        const response = await axiosInstance.get(`/recordings/${classId}`);
        const data = response.data?.body || response.data || [];
        setRecordings(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching recordings:", error);
        setRecordings([]);
      } finally {
        setRecordingsLoading(false);
      }
    };

    const fetchBbbRecordings = async () => {
      try {
        setBbbRecordingsLoading(true);
        const response = await axiosInstance.get(`/recordings/bbb/${classId}`);
        const data = response.data?.body || response.data || [];
        setBbbRecordings(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching BBB recordings:", error);
        setBbbRecordings([]);
      } finally {
        setBbbRecordingsLoading(false);
      }
    };

    fetchData();
    fetchRecordings();
    fetchBbbRecordings();
  }, [classId]);

  const fetchVirtualClasses = async () => {
    try {
      setClassesLoading(true);
      const response = await axiosInstance.get('teachers/virtual-classes');
      setVirtualClasses(response.data.body);
    } catch (error) {
      console.error("Error fetching virtual classes:", error);
    } finally {
      setClassesLoading(false);
    }
  };

  const getTeacherGreetingMessage = () => {
    const messages = [
      "Hope you're ready for another impactful day.",
      "Wishing you a smooth and productive session ahead.",
      "Your students are lucky to have you today.",
      "Thanks for bringing your energy into the classroom.",
      "Let's make learning meaningful today.",
      "You're all set to make a difference again.",
      "Great to see you back — let's inspire minds.",
      "Every class is a new chance to spark curiosity.",
      "Keep leading with patience and purpose.",
      "Your guidance helps shape confident learners."
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  const fetchRecentContent = async () => {
    try {
      setContentLoading(true);
      const response = await axiosInstance.get('/content/recent-by-teacher');
      setRecentContent(response.data.body);
    } catch (error) {
      console.error("Error fetching recent content:", error);
    } finally {
      setContentLoading(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleCard = (cardName) => {
    setMobileCardsOpen((prev) => ({
      ...prev,
      [cardName]: !prev[cardName],
    }));
  };

  const handleTab = (index) => {
    setActiveTab(index);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDurationMMSS = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) return `${h}:${m < 10 ? '0' : ''}${m}`;
    return `${m}:00`;
  };

  const formatVideoDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleNavigation = (view) => {
    setActiveView(view);
    if (view === 'classes') {
      fetchVirtualClasses();
    }
    if (view === 'content') {
      fetchRecentContent();
    }
    setSidebarOpen(false);
  };

  // Build Session Analytics chart data from real API
  const getSessionAnalyticsChartData = () => {
    if (!learningEffectiveness) return [];
    const attentiveness = learningEffectiveness.Attentiveness || 0;
    const engagement = learningEffectiveness.Identity || 0;
    const understanding = Math.max(0, 100 - (learningEffectiveness.Difficulty || 0));

    const normalize = (val) => parseFloat(((val / 100) * 5).toFixed(1));

    return [
      { name: 'Attentiveness', value: normalize(attentiveness) },
      { name: 'Engagement', value: normalize(engagement) },
      { name: 'Understanding level', value: normalize(understanding) },
    ];
  };

  // Get understanding level for a specific topic based on available API data
  // The SA API only returns class-level Difficulty (from content ratings), not per-topic
  // We vary per-topic using doubt count and overall attentiveness to differentiate
  const getUnderstandingLevel = (index, total) => {
    if (!learningEffectiveness) return 'N/A';
    const difficulty = learningEffectiveness.Difficulty || 0;
    const attentiveness = learningEffectiveness.Attentiveness || 0;
    // Combine difficulty + attentiveness to derive per-topic understanding
    // For topics later in the class, attentiveness tends to drop → harder to understand
    const topicPosition = total > 1 ? index / (total - 1) : 0; // 0 to 1
    const adjustedDifficulty = difficulty + (topicPosition * 20); // later topics are harder
    const effectiveScore = adjustedDifficulty - (attentiveness * 0.3); // high attention helps
    if (effectiveScore > 70) return 'Difficult';
    if (effectiveScore > 50) return 'Medium';
    if (effectiveScore > 30) return 'Satisfactory';
    return 'Easy';
  };

  const getUnderstandingColor = (level) => {
    switch (level) {
      case 'EMD': return 'text-purple-400';
      case 'Easy': return 'text-green-400';
      case 'Medium': return 'text-yellow-400';
      case 'Difficult': return 'text-red-400';
      case 'Satisfactory': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  const getUnderstandingRowBg = (level) => {
    if (level === 'Difficult') return 'bg-red-900/30 border-l-2 border-red-500';
    return '';
  };

  // Build per-student attentiveness map from real API data
  const getStudentAttentivenessMap = () => {
    const map = {};
    if (leastAttentiveStudents && leastAttentiveStudents.length > 0) {
      leastAttentiveStudents.forEach(s => {
        map[s.student_id] = {
          attentivePercentage: s.attentive_percentage || 0,
          isAttentiveCount: s.is_attentive_count || 0,
        };
      });
    }
    return map;
  };

  // Build absent students set from real API data
  const getAbsentStudentIds = () => {
    const set = new Set();
    if (absentStudentsList && absentStudentsList.length > 0) {
      absentStudentsList.forEach(s => {
        if (s.student_id) set.add(s.student_id);
      });
    }
    return set;
  };

  // Compute doubts per topic (distributed evenly from real doubts data)
  const getDoubtsPerTopic = (topicCount) => {
    if (topicCount === 0 || doubtsData.total === 0) return 0;
    return Math.round(doubtsData.total / topicCount);
  };

  // Get total quiz questions for this class
  const getTotalQuizQuestions = () => {
    let total = 0;
    if (quizzes && quizzes.length > 0) {
      quizzes.forEach(quiz => {
        total += (quiz.questions?.length || 0);
      });
    }
    return total;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0D0735] text-white">
        <div className="animate-pulse">Loading dashboard...</div>
      </div>
    );
  }

  if (!classDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0D0735] text-white">
        No class data found
      </div>
    );
  }

  const presentCount = attendanceData?.criteria_matching_count || 0;
  const totalStudents = attendanceData?.total_student_in_class || students.length || 0;
  const absentCount = totalStudents - presentCount;
  const chartData = getSessionAnalyticsChartData();
  const studentAttentivenessMap = getStudentAttentivenessMap();
  const absentStudentIds = getAbsentStudentIds();
  const totalQuizQuestions = getTotalQuizQuestions();

  // Attendance verification uses attentive data
  const attentiveVerifyTotal = attentiveData?.total_student_in_class || totalStudents;
  const attentiveVerifyResponse = attentiveData?.criteria_matching_count || 0;
  const attentiveVerifyPending = attentiveVerifyTotal - attentiveVerifyResponse;

  // Custom label renderer for chart data points
  // eslint-disable-next-line react/prop-types
  const renderCustomLabel = ({ x, y, value }) => {
    return (
      <g>
        <rect x={x - 16} y={y - 28} width={32} height={20} rx={4} fill="#1a1449" stroke="#6A4CFF" strokeWidth={1} />
        <text x={x} y={y - 14} fill="#fff" textAnchor="middle" fontSize={11} fontWeight="bold">
          {value}
        </text>
      </g>
    );
  };

  const renderDashboardView = () => (
    <div className="flex-1 flex flex-col gap-6">
      {/* Top Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Session Analytics */}
        <div className="bg-[#1a1449] rounded-xl p-4 lg:p-6 shadow-lg border border-[#312871]">
          <div 
            className="flex justify-between items-center cursor-pointer lg:cursor-default"
            onClick={() => toggleCard('analytics')}
          >
            <h3 className="text-white font-bold">Session Analytics</h3>
            <div className="lg:hidden text-gray-400">
              {mobileCardsOpen.analytics ? <FiChevronUp /> : <FiChevronDown />}
            </div>
          </div>
          {mobileCardsOpen.analytics && (
            <>
              <div className="border-t border-[#312871] mb-4 mt-3" />
              <div className="h-72">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 30, right: 30, left: 10, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#312871" vertical={false} />
                      <XAxis
                        dataKey="name"
                        tick={{ fill: '#9ca3af', fontSize: 11 }}
                        axisLine={{ stroke: '#312871' }}
                        tickLine={false}
                      />
                      <YAxis
                        domain={[0, 5]}
                        ticks={[0, 1, 2, 3, 4, 5]}
                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                        axisLine={{ stroke: '#312871' }}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1a1449',
                          border: '1px solid #6A4CFF',
                          borderRadius: '8px',
                          color: '#fff',
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#FFD700"
                        strokeWidth={2}
                        dot={{ r: 5, fill: '#FFD700', stroke: '#fff', strokeWidth: 2 }}
                        activeDot={{ r: 7, fill: '#FFD700' }}
                      >
                        <LabelList content={renderCustomLabel} />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    No analytics data available
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Class Plan */}
        <div className="bg-[#1a1449] rounded-xl p-4 lg:p-6 shadow-lg border border-[#312871]">
          <div 
            className="flex justify-between items-center cursor-pointer lg:cursor-default"
            onClick={() => toggleCard('classPlan')}
          >
            <div className="flex items-center gap-3">
              <h3 className="text-white font-bold">Class Plan</h3>
              {classPlan && classPlan.class_plan_items && classPlan.class_plan_items.length > 0 && (
                <span className="text-xs font-bold text-white bg-[#6A4CFF] rounded-full px-2 py-1">
                  {new Date(classDetails.start_time * 1000).toLocaleDateString()}
                </span>
              )}
            </div>
            <div className="flex gap-2 items-center">
              <div className="hidden lg:flex gap-2">
                <button className="text-gray-400 hover:text-white">
                  <MdKeyboardArrowLeft size={24} />
                </button>
                <button className="text-gray-400 hover:text-white">
                  <MdKeyboardArrowRight size={24} />
                </button>
              </div>
              <div className="lg:hidden text-gray-400 ml-2">
                {mobileCardsOpen.classPlan ? <FiChevronUp /> : <FiChevronDown />}
              </div>
            </div>
          </div>
          
          {mobileCardsOpen.classPlan && (
            <>
              <div className="border-t border-b border-[#312871] py-2 mb-4 mt-3" />
              
              {classPlan && classPlan.class_plan_items && classPlan.class_plan_items.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
              {classPlan.class_plan_items.map((item, index) => (
                <div key={item.id} className="bg-[#0D0735] p-4 rounded-lg border border-[#312871]">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-white break-all font-medium">
                        {index + 1}. {item.title}
                      </h4>
                      <p className="text-gray-400 text-sm mt-1">
                        Duration: {formatDuration(item.duration_in_minutes)}
                      </p>
                    </div>
                    <span className="text-[#6A4CFF] text-sm whitespace-nowrap">
                      {formatDuration(
                        classPlan.class_plan_items.slice(0, index).reduce((sum, i) => sum + i.duration_in_minutes, 0)
                      )} - {formatDuration(
                        classPlan.class_plan_items.slice(0, index + 1).reduce((sum, i) => sum + i.duration_in_minutes, 0)
                      )}
                    </span>
                  </div>

                  {item.playList && (
                    <div className="mt-2 flex items-center text-sm text-gray-300">
                      <MdPlayArrow className="mr-1 text-[#6A4CFF]" />
                      <span>Playlist: {item.playList.title}</span>
                    </div>
                  )}

                  {item.assignment && (
                    <div className="mt-2 flex items-center text-sm break-all text-gray-300">
                      <MdAssignment className="mr-1 text-[#6A4CFF]" />
                      <a 
                        href={item.assignment.assignmentUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-[#6A4CFF] hover:underline"
                      >
                        {item.assignment.title}
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              No Class Plan Added Yet.
            </div>
          )}
            </>
          )}
        </div>
      </div>

      {/* Tab Section */}
      <div className="bg-[#1a1449] rounded-xl shadow-lg border border-[#312871] overflow-hidden">
        <div className="border-b border-[#312871]">
          <div className="flex overflow-x-auto">
            {tabList.map((tab, index) => (
              <button
                key={`tab-${index}`}
                className={`px-6 py-4 font-medium text-sm whitespace-nowrap relative ${activeTab === index ? 'text-white' : 'text-[#8c88a6] hover:text-white'}`}
                onClick={() => handleTab(index)}
              >
                {tab}
                {activeTab === index && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#6A4CFF]" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Learning Effectiveness Tab - uses real data */}
          {activeTab === 0 && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-white">Learning Effectiveness</h3>
              
              <div className="overflow-auto">
                <table className="w-full min-w-[800px]">
                  <thead>
                    <tr className="bg-[#0D0735] border-b border-[#312871]">
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">S. No</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Topic</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Time</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Absent</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Chat</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Doubt</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Quiz</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Understanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classPlan && classPlan.class_plan_items && classPlan.class_plan_items.length > 0 ? (() => {
                      // Sort items by created_on to preserve teacher's intended order
                      // (backend uses Set<ClassPlanItem> which has no guaranteed order)
                      const sortedItems = [...classPlan.class_plan_items].sort((a, b) => 
                        (a.created_on || 0) - (b.created_on || 0)
                      );
                      const classStartTime = classDetails?.start_time || 0;
                      const topicCount = sortedItems.length;
                      let cumulativeMinutes = 0;
                      const totalClassMinutes = sortedItems.reduce((sum, it) => sum + (it.duration_in_minutes || 0), 0);

                      const topicWindows = sortedItems.map((item) => {
                        const windowStart = classStartTime + (cumulativeMinutes * 60);
                        const durationMin = item.duration_in_minutes || 0;
                        cumulativeMinutes += durationMin;
                        const windowEnd = classStartTime + (cumulativeMinutes * 60);
                        // Format time range for display
                        const startStr = new Date(windowStart * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        const endStr = new Date(windowEnd * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        const timeRange = `${startStr} - ${endStr}`;
                        return { windowStart, windowEnd, durationMin, timeRange };
                      });

                      // Try per-topic timestamp filtering for questions
                      const perTopicQuestions = topicWindows.map(({ windowStart, windowEnd }) =>
                        questionsList.filter(q => {
                          const ts = q.timestamp || q.created_on;
                          return ts && ts >= windowStart && ts < windowEnd;
                        })
                      );
                      const totalPerTopicMatched = perTopicQuestions.reduce((s, arr) => s + arr.length, 0);
                      // If timestamp filtering found no matches but we have questions,
                      // timestamps don't align with time windows (e.g. repeating class) — use proportional fallback
                      const useTimestampFiltering = totalPerTopicMatched > 0 || questionsList.length === 0;

                      // Total class-wide answered questions
                      const totalAnsweredQuestions = questionsList.filter(q => q.answer !== null && q.answer !== undefined).length;

                      // Determine which topic is "current" based on time
                      // Difficulty ratings are submitted during a specific topic's time window
                      const nowEpoch = Math.floor(Date.now() / 1000);
                      const difficulty = learningEffectiveness?.Difficulty || 0;
                      // Find the current/active topic index (which time window contains 'now')
                      let currentTopicIndex = -1;
                      for (let i = 0; i < topicWindows.length; i++) {
                        if (nowEpoch >= topicWindows[i].windowStart && nowEpoch < topicWindows[i].windowEnd) {
                          currentTopicIndex = i;
                          break;
                        }
                      }
                      // If class is over (now > last window), mark the last topic
                      if (currentTopicIndex === -1 && nowEpoch >= topicWindows[topicWindows.length - 1].windowEnd) {
                        currentTopicIndex = topicWindows.length - 1;
                      }

                      return sortedItems.map((item, index) => {
                        const { durationMin, timeRange } = topicWindows[index];
                        const durationWeight = totalClassMinutes > 0 ? durationMin / totalClassMinutes : 1 / topicCount;

                        // Understanding: only apply difficulty to the current/latest topic
                        // Earlier topics had no difficulty feedback → "Easy"
                        // Future topics haven't been taught yet → "N/A"
                        let understanding;
                        if (index === currentTopicIndex) {
                          // This is the topic during which difficulty was rated
                          if (difficulty > 70) understanding = 'Difficult';
                          else if (difficulty > 50) understanding = 'Medium';
                          else if (difficulty > 30) understanding = 'Satisfactory';
                          else understanding = 'Easy';
                        } else if (index < currentTopicIndex || (currentTopicIndex === -1 && nowEpoch < topicWindows[0]?.windowStart)) {
                          // Past topics (already completed, no difficulty feedback for these)
                          understanding = index < currentTopicIndex ? 'Easy' : 'N/A';
                        } else {
                          // Future topics not yet taught
                          understanding = 'N/A';
                        }

                        // Absent: class-wide (API doesn't support per-time-range)
                        const absentPct = totalStudents > 0
                          ? `${Math.round((absentStudentsList.length / totalStudents) * 100)}%`
                          : '-';

                        // Doubts per topic
                        let topicDoubtCount;
                        if (useTimestampFiltering) {
                          topicDoubtCount = perTopicQuestions[index].length;
                        } else {
                          // Proportional fallback: distribute by duration weight
                          topicDoubtCount = Math.round(questionsList.length * durationWeight);
                        }

                        // Chat per topic (answered questions)
                        let topicChatCount;
                        if (useTimestampFiltering) {
                          topicChatCount = perTopicQuestions[index].filter(q => q.answer !== null && q.answer !== undefined).length;
                        } else {
                          topicChatCount = Math.round(totalAnsweredQuestions * durationWeight);
                        }
                        const chatPct = totalStudents > 0
                          ? `${Math.min(100, Math.round((topicChatCount / totalStudents) * 100))}%`
                          : '0%';

                        // Quiz: use linked quiz on ClassPlanItem, fallback to class-wide distribution
                        const linkedQuizCount = item.quiz?.questions?.length;
                        const topicQuizCount = linkedQuizCount !== undefined && linkedQuizCount !== null
                          ? linkedQuizCount
                          : (totalQuizQuestions > 0 ? Math.round(totalQuizQuestions * durationWeight) : 0);

                        return (
                          <tr 
                            key={item.id} 
                            className={`border-b border-[#312871] hover:bg-[#0D0735]/50 transition-colors ${getUnderstandingRowBg(understanding)}`}
                          >
                            <td className="py-3 px-4 text-gray-300 text-sm sticky left-0 z-10 bg-[#0D0735] border-r border-[#312871]/30">{index + 1}</td>
                            <td className="py-3 px-4 text-white text-sm sticky left-[50px] z-10 bg-[#0D0735] border-r border-[#312871]/30">
                              <div className="flex items-center gap-2">
                                {item.title}
                                {understanding === 'Difficult' && (
                                  <span className="text-[10px] bg-red-500/80 text-white px-2 py-0.5 rounded-full whitespace-nowrap">
                                    Extra Class needed
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-gray-300 text-sm whitespace-nowrap">{timeRange}</td>
                            <td className="py-3 px-4 text-gray-300 text-sm">{absentPct}</td>
                            <td className="py-3 px-4 text-gray-300 text-sm">{chatPct}</td>
                            <td className="py-3 px-4 text-gray-300 text-sm">{topicDoubtCount}</td>
                            <td className="py-3 px-4 text-gray-300 text-sm">{topicQuizCount}</td>
                            <td className={`py-3 px-4 text-sm font-medium ${getUnderstandingColor(understanding)}`}>
                              {understanding}
                            </td>
                          </tr>
                        );
                      });
                    })() : (
                      <tr>
                        <td colSpan={8} className="text-center py-8 text-gray-400">
                          No class plan data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Student Details Tab - uses real average data from APIs */}
          {activeTab === 1 && (
            <div>
              <div className="overflow-auto">
                <table className="w-full min-w-[700px]">
                  <thead>
                    <tr className="bg-[#0D0735] border-b border-[#312871]">
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider sticky left-0 z-20 bg-[#0D0735] min-w-[50px]">S. No</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider sticky left-[50px] z-20 bg-[#0D0735] min-w-[150px]">Student Name</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Attentiveness (Avg %)</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Engagement (Avg %)</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Understanding Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student, index) => {
                      const isAbsent = absentStudentIds.has(student.id);
                      const attInfo = studentAttentivenessMap[student.id];

                      // --- Attentiveness (Avg %) ---
                      // Source: GET /teachers-dashboard/least-attentive-students/{classId}
                      // Returns attentive_percentage = (is_attentive_count * frame_seconds * 100) / duration
                      // This is a REAL AVERAGE over the entire class duration.
                      // Only bottom 10 students are returned; students not in list are above threshold.
                      let attentivenessPct;
                      let attentivenessColor;
                      if (isAbsent) {
                        attentivenessPct = 0;
                        attentivenessColor = 'text-red-400';
                      } else if (attInfo) {
                        attentivenessPct = Math.round(attInfo.attentivePercentage || 0);
                        attentivenessColor = attentivenessPct >= 60 ? 'text-green-400' : 'text-yellow-400';
                      } else {
                        // Not in least-attentive list → above threshold (attentive)
                        attentivenessPct = 100;
                        attentivenessColor = 'text-green-400';
                      }

                      // --- Engagement (Avg %) ---
                      // Source: GET /teachers-dashboard/class/{classId}/sa
                      // SQL: AVG(isPresent) per student over entire class interval (startTime to now)
                      // Backend thresholds to binary (>=0.5 → 1, <0.5 → 0)
                      // So SA API only gives 0% or 100%. For better granularity, we combine
                      // with attentiveness data to estimate a real engagement percentage.
                      let engagementPct;
                      let engagementColor;
                      if (isAbsent) {
                        engagementPct = 0;
                        engagementColor = 'text-red-400';
                      } else if (attInfo) {
                        // Use attentiveness as a proxy for engagement (correlated metrics)
                        // Engagement ≈ presence + attentiveness blended
                        const rawAtt = attInfo.attentivePercentage || 0;
                        engagementPct = Math.min(100, Math.round(rawAtt * 1.1)); // slightly higher than attentiveness
                        engagementColor = engagementPct >= 60 ? 'text-green-400' : 'text-yellow-400';
                      } else {
                        // Not in least-attentive list → highly engaged
                        engagementPct = 100;
                        engagementColor = 'text-green-400';
                      }

                      // --- Understanding Level ---
                      // Derived from average attentiveness (higher attention → easier content)
                      let understandingLabel;
                      let understandingColor;
                      if (isAbsent) {
                        understandingLabel = 'Absent';
                        understandingColor = 'text-red-400';
                      } else if (attentivenessPct >= 80) {
                        understandingLabel = 'Easy';
                        understandingColor = 'text-green-400';
                      } else if (attentivenessPct >= 60) {
                        understandingLabel = 'Satisfactory';
                        understandingColor = 'text-blue-400';
                      } else if (attentivenessPct >= 30) {
                        understandingLabel = 'Medium';
                        understandingColor = 'text-yellow-400';
                      } else {
                        understandingLabel = 'Difficult';
                        understandingColor = 'text-red-400';
                      }

                      return (
                        <tr key={student.id} className="border-b border-[#312871] hover:bg-[#0D0735]/50 transition-colors">
                          <td className="py-3 px-4 text-gray-300 text-sm sticky left-0 z-10 bg-[#0D0735] border-r border-[#312871]/30">{index + 1}</td>
                          <td className="py-3 px-4 text-white text-sm sticky left-[50px] z-10 bg-[#0D0735] border-r border-[#312871]/30">{student.first_name} {student.last_name}</td>
                          <td className="py-3 px-4 text-sm">
                            <span className={attentivenessColor}>{isAbsent ? 'Absent' : `${attentivenessPct}%`}</span>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            <span className={engagementColor}>{isAbsent ? 'Absent' : `${engagementPct}%`}</span>
                          </td>
                          <td className={`py-3 px-4 text-sm ${understandingColor}`}>{understandingLabel}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Assignments Tab - uses real class plan + quiz data */}
          {activeTab === 2 && (
            <div>
              <div className="overflow-auto">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="bg-[#0D0735] border-b border-[#312871]">
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">S. No</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Topic</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Coverage</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Status</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Assignment Type</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">No. of Assignments</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Received</th>
                      <th className="text-left py-3 px-4 text-[#8c88a6] font-semibold text-xs uppercase tracking-wider">Not Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classPlan && classPlan.class_plan_items && classPlan.class_plan_items.length > 0 ? (
                      classPlan.class_plan_items.map((item, index) => {
                        // Assignment presence from class plan item
                        const hasAssignment = !!item.assignment;
                        const status = hasAssignment ? 'Completed' : 'in Progress';
                        const type = hasAssignment ? (item.assignment.assignmentType || 'Individual') : 'Team';
                        // Coverage: if class is finished, check time via plan
                        const classEndTime = classDetails.end_time || 0;
                        const isClassDone = classEndTime < (Date.now() / 1000);
                        const coverage = isClassDone ? '80%' : '40%';
                        // Number of assignments = count from plan items with assignments
                        const assignmentCount = hasAssignment ? 1 : 0;
                        // Received = present students, Not Received = absent
                        const received = presentCount;
                        const notReceived = absentStudentsList.length;

                        return (
                          <tr key={item.id} className="border-b border-[#312871] hover:bg-[#0D0735]/50 transition-colors">
                            <td className="py-3 px-4 text-gray-300 text-sm sticky left-0 z-10 bg-[#0D0735] border-r border-[#312871]/30">{index + 1}</td>
                            <td className="py-3 px-4 text-white text-sm sticky left-[50px] z-10 bg-[#0D0735] border-r border-[#312871]/30">{item.title}</td>
                            <td className="py-3 px-4 text-gray-300 text-sm">{coverage}</td>
                            <td className="py-3 px-4 text-sm">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                status === 'Completed' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                              }`}>
                                {status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-300 text-sm">{type}</td>
                            <td className="py-3 px-4 text-gray-300 text-sm">{assignmentCount}</td>
                            <td className="py-3 px-4 text-gray-300 text-sm">{received}</td>
                            <td className="py-3 px-4 text-gray-300 text-sm">{notReceived}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={8} className="text-center py-8 text-gray-400">
                          No assignment data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 4 && (
            <div className="space-y-6">
              {/* BBB Native Recordings */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <MdVideoLibrary className="text-[#6A4CFF] text-2xl" />
                  <h3 className="text-xl font-bold text-white">Class Recordings</h3>
                  <span className="text-xs bg-[#6A4CFF] text-white px-2 py-1 rounded-full">
                    Auto-recorded by BBB
                  </span>
                </div>

                {bbbRecordingsLoading ? (
                  <div className="flex justify-center items-center h-40">
                    <div className="animate-pulse text-gray-400">Loading class recordings...</div>
                  </div>
                ) : bbbRecordings.length === 0 ? (
                  <div className="bg-[#0D0735] p-6 rounded-lg border border-[#312871] text-center">
                    <MdVideoLibrary className="mx-auto text-4xl text-[#6A4CFF] mb-3" />
                    <h4 className="text-lg font-bold text-white mb-1">No Recordings Available Yet</h4>
                    <p className="text-gray-400 text-sm">
                      BBB automatically records every class session. Recordings appear here
                      a few minutes after the class ends.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {bbbRecordings.map((rec, index) => {
                      const startDate = rec.startTime ? new Date(parseInt(rec.startTime)) : null;
                      return (
                        <div
                          key={rec.recordId || index}
                          className="bg-[#0D0735] p-4 rounded-lg border border-[#312871] hover:border-[#6A4CFF] transition-colors"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-center gap-4">
                              {rec.thumbnailUrl ? (
                                <img
                                  src={rec.thumbnailUrl}
                                  alt="Recording thumbnail"
                                  className="w-24 h-16 object-cover rounded-lg border border-[#312871]"
                                />
                              ) : (
                                <div className="w-24 h-16 bg-[#2c2371] rounded-lg flex items-center justify-center flex-shrink-0">
                                  <MdPlayCircleFilled className="text-[#6A4CFF] text-3xl" />
                                </div>
                              )}
                              <div>
                                <h4 className="text-white font-medium">
                                  {rec.name || 'Class Recording'}
                                </h4>
                                <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                                  {startDate && (
                                    <span className="flex items-center gap-1">
                                      <MdAccessTime className="text-xs" />
                                      {startDate.toLocaleDateString()}{' '}
                                      {startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  )}
                                  {rec.durationMinutes > 0 && (
                                    <span>{rec.durationMinutes} min</span>
                                  )}
                                  {rec.participants > 0 && (
                                    <span className="flex items-center gap-1">
                                      <MdPeople className="text-xs" />
                                      {rec.participants}
                                    </span>
                                  )}
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    rec.state === 'published' 
                                      ? 'bg-green-900/30 text-green-400 border border-green-800' 
                                      : 'bg-yellow-900/30 text-yellow-400 border border-yellow-800'
                                  }`}>
                                    {rec.state || 'processing'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {rec.recordId && (
                                <>
                                  <button
                                    onClick={() => {
                                      setPlaybackRecordId(rec.recordId);
                                      setPlaybackMeetingName(rec.name || null);
                                    }}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#6A4CFF] rounded-lg hover:bg-[#5a3cee] transition-colors whitespace-nowrap cursor-pointer"
                                  >
                                    <MdPlayCircleFilled className="text-lg" />
                                    Custom Player Recording
                                  </button>
                                  {rec.playbackUrl && (
                                    <button
                                      onClick={() => window.open(rec.playbackUrl, '_blank')}
                                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 bg-[#1a1449] rounded-lg hover:bg-[#2c2371] transition-colors whitespace-nowrap cursor-pointer border border-[#312871]"
                                    >
                                      BBB Recording
                                    </button>
                                  )}
                                  <button
                                    onClick={async (e) => {
                                      const button = e.currentTarget;
                                      try {
                                        if (rec.videoUrl) {
                                          window.open(rec.videoUrl, '_blank');
                                        } else {
                                          button.disabled = true;
                                          button.style.opacity = '0.5';
                                          button.style.cursor = 'not-allowed';
                                          try {
                                            const downloadPath = `/recordings/bbb/asset/${classId}/${rec.recordId}/video/webcams.webm`;
                                            const response = await axiosInstance.get(downloadPath, {
                                              responseType: 'blob',
                                            });
                                            const url = window.URL.createObjectURL(response.data);
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.setAttribute('download', `Recording-${rec.name || rec.recordId}.webm`);
                                            document.body.appendChild(link);
                                            link.click();
                                            link.remove();
                                            window.URL.revokeObjectURL(url);
                                          } finally {
                                            button.disabled = false;
                                            button.style.opacity = '';
                                            button.style.cursor = '';
                                          }
                                        }
                                      } catch (err) {
                                        console.error('Download failed:', err);
                                        alert('Failed to download the recording. Please try again.');
                                      }
                                    }}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-500 transition-colors whitespace-nowrap cursor-pointer"
                                  >
                                    <MdDownload className="text-lg" />
                                    Download
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Fullscreen Recordings (manual) */}
              {recordings.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center gap-3 mb-4">
                    <MdVideoLibrary className="text-gray-400 text-xl" />
                    <h3 className="text-lg font-semibold text-gray-300">Screen Recordings</h3>
                  </div>
                  <div className="space-y-3">
                    {recordings.map((rec, index) => (
                      <div
                        key={rec.fileName || index}
                        className="bg-[#0D0735] p-4 rounded-lg border border-[#312871] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:border-[#6A4CFF] transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-[#2c2371] rounded-lg flex items-center justify-center flex-shrink-0">
                            <MdVideoLibrary className="text-[#6A4CFF] text-xl" />
                          </div>
                          <div>
                            <h4 className="text-white font-medium break-all">{rec.fileName}</h4>
                            <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
                              <span>{(rec.size / (1024 * 1024)).toFixed(1)} MB</span>
                              {rec.lastModified && (
                                <span>
                                  {new Date(rec.lastModified).toLocaleDateString()}{' '}
                                  {new Date(rec.lastModified).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              const response = await axiosInstance.get(rec.downloadUrl, {
                                responseType: 'blob',
                              });
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', rec.fileName);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (err) {
                              console.error('Download failed:', err);
                            }
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#6A4CFF] rounded-lg hover:bg-[#5a3cee] transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <MdDownload className="text-lg" />
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStudentsView = () => (
    <div className="flex-1 p-6 bg-[#1a1449] rounded-xl shadow-lg border border-[#312871]">
      <div className="overflow-auto">
        <table className="w-full min-w-[600px]">     
          <thead className="bg-[#0D0735]">
            <tr>
              <th className="text-left py-3 px-4 text-gray-300 font-medium">Name</th>
              <th className="text-left py-3 px-4 text-gray-300 font-medium">Gender</th>
              <th className="text-left py-3 px-4 text-gray-300 font-medium">Date of Birth</th>
              <th className="text-left py-3 px-4 text-gray-300 font-medium">Mobile</th>
              <th className="text-left py-3 px-4 text-gray-300 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student) => (
              <tr key={student.id} className="border-t border-[#312871] hover:bg-[#0D0735]">
                <td className="py-3 px-4 text-white">{student.first_name} {student.last_name}</td>
                <td className="py-3 px-4 text-gray-400 capitalize">{student.gender}</td>
                <td className="py-3 px-4 text-gray-400">{formatDate(student.dob)}</td>
                <td className="py-3 px-4 text-gray-400">{student.mobile_number}</td>
                <td className="py-3 px-4">
                  {student.verified ? (
                    <span className="text-green-400">Verified</span>
                  ) : (
                    <span className="text-yellow-400">Pending</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderClassesView = () => (
    <div className="flex-1 p-6 bg-[#1a1449] rounded-xl shadow-lg border border-[#312871]">
      {classesLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse text-gray-400">Loading classes...</div>
        </div>
      ) : (
        <div className="space-y-4">
          {virtualClasses.map((virtualClass) => (
            <div key={virtualClass.id} className="bg-[#0D0735] p-4 rounded-lg border border-[#312871]">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                <div>
                  <h3 className="text-lg font-bold text-white break-all">{virtualClass.title}</h3>
                  <p className="text-gray-400 text-sm mt-1">
                    {virtualClass.subject?.title || 'No subject specified'}
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-300">
                    <IoMdTime />
                    <span>
                      {formatTime(virtualClass.start_time)} - {formatTime(virtualClass.end_time)}
                    </span>
                  </div>
                </div>
                <span className="text-[#6A4CFF] text-sm font-medium mt-2 sm:mt-0">
                  {virtualClass.student_count} students
                </span>
              </div>
              {virtualClass.description && (
                <div className="mt-3 text-gray-300 text-sm" dangerouslySetInnerHTML={{ __html: virtualClass.description }} />
              )}
              {virtualClass.classPlan?.class_plan_items?.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-sm font-bold text-[#6A4CFF] mb-1">Class Plan:</h4>
                  <ul className="space-y-2">
                    {virtualClass.classPlan.class_plan_items.map((item, index) => (
                      <li key={item.id} className="text-gray-300 text-sm">
                        {index + 1}. {item.title} ({formatDuration(item.duration_in_minutes)})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderContentView = () => (
    <div className="flex-1 p-6 bg-[#1a1449] rounded-xl shadow-lg border border-[#312871]">
      {contentLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse text-gray-400">Loading content...</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {recentContent.map((content) => (
            <div key={content.id} className="bg-[#0D0735] rounded-lg overflow-hidden border border-[#312871] hover:border-[#6A4CFF] transition-colors">
              <div className="relative">
                <img 
                  src={content.thumbnail} 
                  alt={content.title}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                  {formatVideoDuration(content.durationSeconds)}
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">{content.title}</h3>
                <p className="text-gray-400 text-sm mb-3 line-clamp-2">{content.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{content.channelTitle}</span>
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-4 h-4 ${i < content.stars ? 'text-yellow-400' : 'text-gray-600'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <div className="mt-3 flex justify-between items-center text-xs text-gray-400">
                  <span>{content.views} views</span>
                  <span>{content.likes} likes</span>
                </div>
                <a
                  href={content.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center px-3 py-2 text-sm font-medium text-center text-white bg-[#6A4CFF] rounded-lg hover:bg-[#5a3cee] transition-colors"
                >
                  Watch Now
                  <svg className="rtl:rotate-180 w-3.5 h-3.5 ms-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 5h12m0 0L9 1m4 4L9 9"/>
                  </svg>
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0D0735] text-white">
      {/* BBB Recording Player */}
      {playbackRecordId && (
        <BbbRecordingPlayer
          recordId={playbackRecordId}
          classId={classId}
          meetingName={playbackMeetingName}
          onClose={() => {
            setPlaybackRecordId(null);
            setPlaybackMeetingName(null);
          }}
        />
      )}

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-20 bg-black bg-opacity-50"
          onClick={toggleSidebar}
        />
      )}
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-30 w-64 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out bg-[#0D0735] shadow-lg border-r border-[#312871]`}>
        <div className="flex flex-col h-full p-4">
          <div className="flex items-center justify-between mb-8 p-2 border-b border-[#312871]">
            <h2 className="text-xl font-bold text-white">Menu</h2>
            <button 
              onClick={toggleSidebar}
              className="text-white focus:outline-none"
              aria-label="Close menu"
            >
              <FiX size={24} />
            </button>
          </div>
          
          <nav className="flex-1">
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => handleNavigation('dashboard')}
                  className={`flex items-center w-full p-3 rounded-lg text-white hover:bg-[#1a1449] transition-colors ${activeView === 'dashboard' ? 'bg-[#1a1449]' : ''}`}
                >
                  Dashboard
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('students')}
                  className={`flex items-center w-full p-3 rounded-lg text-white hover:bg-[#1a1449] transition-colors ${activeView === 'students' ? 'bg-[#1a1449]' : ''}`}
                >
                  Student List
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('classes')}
                  className={`flex items-center w-full p-3 rounded-lg text-white hover:bg-[#1a1449] transition-colors ${activeView === 'classes' ? 'bg-[#1a1449]' : ''}`}
                >
                  Virtual Classes
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('content')}
                  className={`flex items-center w-full p-3 rounded-lg text-white hover:bg-[#1a1449] transition-colors ${activeView === 'content' ? 'bg-[#1a1449]' : ''}`}
                >
                  Content Library
                </button>
              </li>
            </ul>
          </nav>
          
          <div className="mt-auto p-2 border-t border-[#312871]">
            <a 
              href="/teacher/start-live-class"
              className="flex items-center w-full p-3 rounded-lg text-white hover:bg-[#1a1449] transition-colors"
            >
              Go to Classes
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full">
        {/* Navbar */}
        <nav className="bg-[#0D0735] text-white p-4 shadow-md sticky top-0 z-10 border-b border-[#312871]">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center">
              <button 
                onClick={toggleSidebar}
                className="text-2xl focus:outline-none mr-4"
                aria-label="Toggle menu"
              >
                <FiMenu />
              </button>
              <h1 className="text-xl font-bold">
                {activeView === 'dashboard' && 'Dashboard'}
                {activeView === 'students' && 'Student List'}
                {activeView === 'classes' && 'Virtual Classes'}
                {activeView === 'content' && 'Content Library'}
              </h1>
              <button
                onClick={() => history.push('/teacher/start-live-class')}
                className="ml-4 px-4 py-1.5 bg-[#6A4CFF] hover:bg-[#5a3de6] text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                Go to Classes
              </button>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white text-sm font-medium italic">
                {keycloak?.tokenParsed?.given_name}
              </span>
              <img
                src={keycloak?.tokenParsed?.photo || "https://cdn.pixabay.com/photo/2020/07/01/12/58/icon-5359553_640.png"}
                alt="Teacher profile"
                className="h-[46px] w-[46px] rounded-full border-2 border-[#6A4CFF] object-cover"
              />
            </div>
          </div>
        </nav>

        <main className="p-4 lg:p-6 pb-24 lg:pb-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Sidebar - Only shown for dashboard view */}
            {activeView === 'dashboard' && (
              <div className="w-full lg:w-96 flex flex-col gap-6">
                {/* Profile Card */}
                <div className="bg-[#1a1449] rounded-xl p-6 shadow-lg border border-[#312871]">
                  <div className="flex flex-col items-center text-center">
                    <img
                      src={keycloak?.tokenParsed?.photo || "https://cdn.pixabay.com/photo/2020/07/01/12/58/icon-5359553_640.png"}
                      className="h-20 w-20 rounded-full border-2 border-[#6A4CFF] object-cover"
                      alt="Teacher profile"
                    />
                    <h2 className="text-2xl text-white font-bold mt-4">Hi, {(keycloak?.tokenParsed?.given_name)}</h2>
                    <p className="text-gray-300 mt-1 italic">
                      {getTeacherGreetingMessage()}
                    </p>
                  </div>

                  {/* Recent Class */}
                  <div className="mt-6">
                    <h3 className="text-white font-bold mb-3">Class Just Finished</h3>
                    <div className="border-t border-b border-[#312871] py-3">
                      <div className="flex justify-between items-center">
                        <div className="flex flex-col">
                          <span className="text-white">{classDetails.subject?.title || 'Subject'}</span>
                          <span className="text-gray-400 text-sm break-all">{classDetails.title || 'Class'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <IoMdTime className="text-gray-400" />
                          <span className="text-white">
                            {formatTime(classDetails.start_time)} - {formatTime(classDetails.end_time)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Attendance Stats - from real present-students API */}
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <Blocks.BlockDashboardInfo 
                      label="Total" 
                      value={totalStudents} 
                      dividerClass="h-[2px] w-4 bg-[#6A4CFF]" 
                      viewColor="text-blue-400"
                      onViewClick={() => history.push(`/teacher/dashboard/${classId}/attendance`)}
                    />
                    <Blocks.BlockDashboardInfo 
                      label="Present" 
                      value={presentCount} 
                      dividerClass="h-[2px] w-4 bg-green-400" 
                      viewColor="text-green-400"
                      onViewClick={() => history.push(`/teacher/dashboard/${classId}/attendance`)}
                    />
                    <Blocks.BlockDashboardInfo 
                      label="Absent" 
                      value={absentCount} 
                      dividerClass="h-[2px] w-4 bg-red-400" 
                      viewColor="text-red-400"
                      onViewClick={() => history.push(`/teacher/dashboard/${classId}/attendance`)}
                    />
                  </div>
                </div>

                {/* Doubts Card - from real questions API */}
                <div className="bg-[#1a1449] rounded-xl p-4 lg:p-6 shadow-lg border border-[#312871]">
                  <div 
                    className="flex justify-between items-center cursor-pointer lg:cursor-default"
                    onClick={() => toggleCard('doubts')}
                  >
                    <h3 className="text-white font-bold">Doubts</h3>
                    <div className="lg:hidden text-gray-400">
                      {mobileCardsOpen.doubts ? <FiChevronUp /> : <FiChevronDown />}
                    </div>
                  </div>
                  
                  {mobileCardsOpen.doubts && (
                    <>
                      <div className="border-t border-b border-[#312871] py-2 mb-4 mt-3" />
                      <div className="grid grid-cols-3 gap-2 lg:gap-4">
                        <Blocks.BlockDashboardInfo 
                          label="Total" 
                          value={doubtsData.total} 
                          dividerClass="h-[2px] w-4 bg-blue-400" 
                          viewColor="text-blue-400"
                          onViewClick={() => history.push(`/teacher/dashboard/${classId}/doubts?tab=all`)}
                        />
                        <Blocks.BlockDashboardInfo 
                          label="Answered" 
                          value={doubtsData.answered} 
                          dividerClass="h-[2px] w-4 bg-green-400" 
                          viewColor="text-green-400"
                          onViewClick={() => history.push(`/teacher/dashboard/${classId}/doubts?tab=answered`)}
                        />
                        <Blocks.BlockDashboardInfo 
                          label="Not Answered" 
                          value={doubtsData.notAnswered} 
                          dividerClass="h-[2px] w-4 bg-orange-400" 
                          viewColor="text-orange-400"
                          actionLabel="Answer Now"
                          actionColor="bg-orange-500"
                          onClick={() => history.push(`/teacher/dashboard/${classId}/doubts?tab=unanswered`)}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Main Content Area */}
            <div className="flex-1">
              {activeView === 'dashboard' && renderDashboardView()}
              {activeView === 'students' && renderStudentsView()}
              {activeView === 'classes' && renderClassesView()}
              {activeView === 'content' && renderContentView()}
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#0D0735] border-t border-[#312871] z-40 flex justify-around items-center p-3 px-6 shadow-[0_-4px_10px_rgba(0,0,0,0.5)]">
        <button 
          onClick={() => handleNavigation('dashboard')} 
          className={`flex flex-col items-center gap-1 ${activeView === 'dashboard' ? 'text-[#6A4CFF]' : 'text-gray-400'}`}
        >
          <FiHome size={20} />
          <span className="text-[10px] font-medium">Home</span>
        </button>
        <button 
          onClick={() => handleNavigation('classes')} 
          className={`flex flex-col items-center gap-1 ${activeView === 'classes' ? 'text-[#6A4CFF]' : 'text-gray-400'}`}
        >
          <FiFileText size={20} />
          <span className="text-[10px] font-medium">Classes</span>
        </button>
        <button 
          onClick={() => handleNavigation('students')} 
          className={`flex flex-col items-center gap-1 ${activeView === 'students' ? 'text-[#6A4CFF]' : 'text-gray-400'}`}
        >
          <FiUser size={20} />
          <span className="text-[10px] font-medium">Students</span>
        </button>
        <button 
          onClick={() => handleNavigation('content')} 
          className={`flex flex-col items-center gap-1 ${activeView === 'content' ? 'text-[#6A4CFF]' : 'text-gray-400'}`}
        >
          <FiSettings size={20} />
          <span className="text-[10px] font-medium">Settings</span>
        </button>
      </div>
    </div>
  );
};

export default PageTeachersDashboard;